import {Component, Inject, OnInit, Optional} from '@angular/core';
import {FormControl, FormGroup, FormArray, FormBuilder, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AutoUnsubscribe} from '../../../../infrastructure/decorators/auto-unsubscribe.decorator';
import {CommissionRulesService} from '../../service/commission-rules.service';
import {
    CommissionRuleCriteria,
    CommissionRuleConditionOperator,
    CommissionRuleDetail,
    CommissionRuleUiComponent,
    CommissionRuleActionType
} from '../../../shared/model/commission-rules-result';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Subscription} from 'rxjs';

@AutoUnsubscribe()
@Component({
    selector: 'commission-rule-modal',
    templateUrl: './commission-rule-modal.component.html',
    standalone: false
})
export class CommissionRuleModalComponent implements OnInit {
    criteria: CommissionRuleCriteria[] = [];
    operators: CommissionRuleConditionOperator[] = [];
    actionTypes: CommissionRuleActionType[] = [];
    uiComponents: CommissionRuleUiComponent[] = [];
    ruleDetail?: CommissionRuleDetail;
    criteriaSelect$?: Subscription;

    form: FormGroup = new FormGroup({
        ruleName: new FormControl(null, [Validators.required]),
        description: new FormControl(null),
        criteriaId: new FormControl(null, [Validators.required]),
        priority: new FormControl(100, [Validators.required, Validators.min(1)]),
        autoFixDelayMinutes: new FormControl(120, [Validators.required, Validators.min(0)]),
        enabled: new FormControl(true),
        paused: new FormControl(false),
        expression: new FormControl('', [Validators.required]),
        expectedValueExpression: new FormControl(''),
        actionTypeId: new FormControl(3, [Validators.required]),
        conditions: new FormArray([]),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public dialogData: string,
        @Optional() public dialogRef: MatDialogRef<CommissionRuleModalComponent>,
        private fb: FormBuilder,
        private commissionRulesService: CommissionRulesService,
        private _snackBar: MatSnackBar,
    ) {}

    get conditions(): FormArray {
        return this.form.get('conditions') as FormArray;
    }

    ngOnInit(): void {
        this.commissionRulesService.getCriteria().subscribe((data) => {
            this.criteria = data;
        });

        this.commissionRulesService.getOperators().subscribe((data) => {
            this.operators = data;
        });

        this.commissionRulesService.getActionTypes().subscribe((data) => {
            this.actionTypes = data;
        });

        if (this.dialogData) {
            this.commissionRulesService.getRuleDetails(this.dialogData).subscribe((data) => {
                this.ruleDetail = data;
                this.form.patchValue({
                    ruleName: data.workflow.name,
                    description: data.workflow.description,
                    criteriaId: `${data.workflow.criteriaId}`,
                    priority: data.workflow.priority,
                    autoFixDelayMinutes: data.workflow.autoFixDelayMinutes,
                    enabled: data.workflow.isEnabled,
                    paused: data.workflow.isPaused,
                });

                if (data.workflow.workflowRules?.length > 0) {
                    const rule = data.workflow.workflowRules[0];
                    this.form.patchValue({
                        expression: rule.expression,
                        expectedValueExpression: rule.expectedValueExpression,
                    });

                    if (rule.workflowRuleActions?.length > 0) {
                        this.form.patchValue({
                            actionTypeId: rule.workflowRuleActions[0].typeId,
                        });
                    }

                    rule.workflowRuleConditions?.forEach((condition) => {
                        this.conditions.push(this.fb.group({
                            id: condition.id,
                            entityName: condition.entityName,
                            entityColumn: condition.entityColumn,
                            operatorId: condition.workflowLogicOperatorId,
                            value: condition.workflowRuleConditionValue,
                        }));
                    });
                }

                this.loadUiComponents(data.workflow.criteriaId.toString());
                this.form.controls['criteriaId'].disable();
            });
        }

        this.criteriaSelect$ = this.form.controls['criteriaId'].valueChanges.subscribe((criteriaId) => {
            if (criteriaId) {
                this.loadUiComponents(criteriaId);
            }
        });
    }

    loadUiComponents(criteriaId: string) {
        this.commissionRulesService.getUiComponents(criteriaId).subscribe((data) => {
            this.uiComponents = data;

            if (!this.ruleDetail && this.conditions.length === 0) {
                data.forEach((component) => {
                    this.conditions.push(this.fb.group({
                        entityName: component.entityName,
                        entityColumn: component.entityColumn,
                        operatorId: [5],
                        value: [''],
                    }));
                });
            }
        });
    }

    addCondition() {
        this.conditions.push(this.fb.group({
            entityName: [''],
            entityColumn: [''],
            operatorId: [5],
            value: [''],
        }));
    }

    removeCondition(index: number) {
        this.conditions.removeAt(index);
    }

    applyRule() {
        const formValue = this.form.getRawValue();

        const workflowRuleConditions = formValue.conditions.map((c: any, i: number) => ({
            ...(c.id ? {id: c.id} : {}),
            workflowLogicOperatorId: c.operatorId,
            workflowRuleConditionValue: `${c.value}`,
            entityName: c.entityName,
            entityColumn: c.entityColumn,
            orderNumber: i + 1,
        }));

        const workflowRule = {
            ...(this.ruleDetail?.workflow?.workflowRules?.[0]?.id
                ? {id: this.ruleDetail.workflow.workflowRules[0].id} : {}),
            ruleName: formValue.ruleName,
            description: formValue.description,
            expression: formValue.expression,
            expectedValueExpression: formValue.expectedValueExpression,
            successEvent: formValue.ruleName,
            errorMessage: `${formValue.ruleName} violation detected`,
            isEnabled: true,
            workflowRuleConditions,
            workflowRuleActions: [
                {
                    ...(this.ruleDetail?.workflow?.workflowRules?.[0]?.workflowRuleActions?.[0]?.id
                        ? {id: this.ruleDetail.workflow.workflowRules[0].workflowRuleActions[0].id} : {}),
                    typeId: formValue.actionTypeId,
                    name: formValue.ruleName,
                }
            ],
        };

        if (this.ruleDetail) {
            const request = {
                id: this.ruleDetail.workflow.id,
                name: formValue.ruleName,
                description: formValue.description,
                categoryId: 3,
                criteriaId: parseInt(formValue.criteriaId, 10),
                isEnabled: formValue.enabled,
                isPaused: formValue.paused,
                priority: formValue.priority,
                autoFixDelayMinutes: formValue.autoFixDelayMinutes,
                workflowRules: [
                    {
                        ...workflowRule,
                        workflowId: this.ruleDetail.workflow.id,
                    }
                ],
            };

            this.commissionRulesService.updateRule(request).subscribe(() => {
                this.dialogRef.close(true);
                this._snackBar.open('Commission rule updated', 'Dismiss', {
                    horizontalPosition: 'center', verticalPosition: 'bottom',
                });
            });
        } else {
            const request = {
                name: formValue.ruleName,
                description: formValue.description,
                categoryId: 3,
                criteriaId: parseInt(formValue.criteriaId, 10),
                isEnabled: formValue.enabled,
                isPaused: formValue.paused,
                priority: formValue.priority,
                autoFixDelayMinutes: formValue.autoFixDelayMinutes,
                workflowRules: [workflowRule],
            };

            this.commissionRulesService.createRule(request).subscribe(() => {
                this.dialogRef.close(true);
                this._snackBar.open('Commission rule created', 'Dismiss', {
                    horizontalPosition: 'center', verticalPosition: 'bottom',
                });
            });
        }
    }

    handleClose(): void {
        this.dialogRef?.close();
    }

    getOperatorDisplayName(operatorId: number): string {
        return this.operators.find(o => o.id === operatorId)?.displayName || '';
    }
}
